import { Duration, endOfWeek, format, getWeekOfMonth, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';

import chargingCompletedImg from '../../assets/images/evse_charging_completed.svg';
import chargingFailedImg from '../../assets/images/evse_charging_failed.svg';
import chargingInProgressImg from '../../assets/images/evse_charging_in_progress.svg';
import chargingPausedImg from '../../assets/images/evse_charging_paused.svg';
import chargingUnavailableImg from '../../assets/images/evse_charging_unavailable.svg';
import { DateRangeType } from '../../common/components/DateRangeTypePicker';
import { ChargerConfiguration, ChargingActivitySessionData, QRCodeValues } from './types';

function getSnFromSsid(ssid: string): string {
  const parts = ssid.split('_');
  return parts[parts.length - 1] || '';
}

export function parseQRCode(content: string): QRCodeValues | null {
  // QR code content sample:
  // WIFI:S:SECharge_123;T:WPA;P:pw_for_WPA-PSK_or_WPA2-PSK;H:FALSE;CPID=11a2222-3333-4444-5555-66bc77b888d9;
  if (!content) {
    return null;
  }

  content = content.replace('WIFI:', '');
  content = content.replace(/CPID=/i, 'CPID:');

  // Split by unescaped semicolons.
  const splittedChargerProperties = content.split(/(?<!\\);/);

  const codeValues = splittedChargerProperties.reduce(
    (acc, curr) => {
      const [key, ...valueParts] = curr.split(':');
      let value = valueParts.join(':'); // Rejoin in case value contains colons.
      if (['S', 'P', 'CPID'].includes(key.toUpperCase())) {
        // Unescape special characters.
        value = value
          .replace(/\\:/g, ':')
          .replace(/\\;/g, ';')
          .replace(/\\,/g, ',')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');

        return { ...acc, [key.toUpperCase()]: value };
      }
      return acc;
    },
    { S: '', P: '', CPID: '' }
  );

  return {
    cpid: codeValues.CPID,
    sn: getSnFromSsid(codeValues.S),
    wifi:
      !codeValues.S || !codeValues.P
        ? null
        : {
            ssid: codeValues.S,
            password: codeValues.P,
          },
  };
}

export function getTestingQRCodeContent(useInvalidData = false): string {
  const sn = useInvalidData ? '' : 'EVB1A33P4KI3N214912002400150027AZ';
  const cpid = '0586996e-3d14-403d-9b71-31012010e7f21';
  const wifiSsid = useInvalidData ? '' : `SECharge_${sn}`;
  const wifiPassword = 'fake_password';
  return `WIFI:S:${wifiSsid};T:WPA;P:${wifiPassword};H:false;cpid=${cpid};`;
}

export const CHARGER_STATUS_UI_CONFIG: Record<ChargerStatus, ChargerConfiguration> = {
  Available: {
    imgSrc: {
      light: chargingCompletedImg,
      dark: chargingCompletedImg,
    },
  },
  Preparing: {
    imgSrc: {
      light: chargingInProgressImg,
      dark: chargingInProgressImg,
    },
  },
  Charging: {
    imgSrc: {
      light: chargingInProgressImg,
      dark: chargingInProgressImg,
    },
  },
  SuspendedEV: {
    imgSrc: {
      light: chargingPausedImg,
      dark: chargingPausedImg,
    },
  },
  SuspendedEVSE: {
    imgSrc: {
      light: chargingPausedImg,
      dark: chargingPausedImg,
    },
  },
  Finishing: {
    imgSrc: {
      light: chargingPausedImg,
      dark: chargingPausedImg,
    },
  },
  Unavailable_Disconnect: {
    imgSrc: {
      light: chargingUnavailableImg,
      dark: chargingUnavailableImg,
    },
  },
  Unavailable_Restart: {
    imgSrc: {
      light: chargingUnavailableImg,
      dark: chargingUnavailableImg,
    },
  },
  Unavailable_Charger: {
    imgSrc: {
      light: chargingUnavailableImg,
      dark: chargingUnavailableImg,
    },
  },
  Faulted: {
    imgSrc: {
      light: chargingFailedImg,
      dark: chargingFailedImg,
    },
  },
};

/**
 * Formats seconds into a string representing the total time in "H:MM" format.
 * The days are converted to hours and added to the hours property.
 *
 * @param sessionTime - The total session time in seconds.
 * @returns A string representing the total time in hours and minutes.
 */
export function formatTotalTime(sessionTime: number): string {
  const duration: Duration = {
    hours: Math.floor(sessionTime / 3600),
    minutes: Math.floor((sessionTime % 3600) / 60),
    seconds: sessionTime % 60,
  };

  const days = duration.days || 0;
  const hours = duration.hours || 0;
  const minutes = duration.minutes || 0;

  return `${days * 24 + hours}:${minutes < 10 ? '0' + minutes : minutes}`;
}

export function getDateRangeTypeLabel(dateRangeType: DateRangeType, start: string, chargingSessionId: number) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation();

  if (!start) return '';

  switch (dateRangeType) {
    case DateRangeType.Day:
      return `${t('Common.session')} ${chargingSessionId}`;

    case DateRangeType.Week:
      return format(start, 'EEEE');

    case DateRangeType.Month:
      return `${t('Common.week')} ${getWeekOfMonth(new Date(start))}`;

    case DateRangeType.Year:
      return format(start, 'LLLL');

    default:
      return '';
  }
}

/**
 * Generates an ARIA label for a given date range type and start/end dates.
 *
 * @param dateRangeType - The type of date range (Day, Week, Month, Year).
 * @param start - The start date as a string.
 * @param end - The end date as a string.
 * @returns A formatted ARIA label string based on the date range type:
 * if DAY = Mon, 8/24/24, 2:00-7:30pm
 * if WEEK = 4/4/24, 12:00-6:00am
 * if MONTH = 4/1/25 - 4/5/25
 * if YEAR = 1/1/25 -1/31/25
 */
export function getDateRangeTypeAriaLabel(dateRangeType: DateRangeType, start: string, end: string): string {
  if (!start || !end) return '';

  // there is inconsistency for start and end format
  // with this we are assuring that UI is getting always same format
  // yyyy-MM-dd'T'HH:mm:ss
  const startTrimmed = start.slice(0, 19);
  const endTrimmed = end.slice(0, 19);
  const weekEnd = endOfWeek(start, { weekStartsOn: 1 });
  const localStartDate = parse(startTrimmed, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  const localEndDate = parse(endTrimmed, "yyyy-MM-dd'T'HH:mm:ss", new Date());

  switch (dateRangeType) {
    case DateRangeType.Day:
      return `${format(start, 'EE, M/d/yy')}, ${format(localStartDate, 'h:mm')}-${format(localEndDate, 'h:mm aa')}`;

    case DateRangeType.Week:
      return `${format(start, 'M/d/yy')}, ${format(localStartDate, 'h:mm')}-${format(localEndDate, 'h:mm aa')}`;

    case DateRangeType.Month:
      return `${format(start, 'M/d/yy')} - ${format(weekEnd, 'M/d/yy')}`;

    case DateRangeType.Year:
      return `${format(start, 'M/d/yy')} - ${format(end, 'M/d/yy')}`;

    default:
      return '';
  }
}

export function formatSecondsToHHMM(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (!minutes) {
    return `${hours}`;
  }
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

export function groupByWeeks(records: ChargingActivitySessionData[]): ChargingActivitySessionData[] {
  const weeks: Record<number, ChargingActivitySessionData> = {};

  for (const r of records) {
    if (!r.start) break;

    const id: number = getWeekOfMonth(new Date(r.start), { weekStartsOn: 1 });
    if (!weeks[id]) {
      weeks[id] = {
        id,
        start: r.start,
        end: r.start,
        usage: 0,
        cost: 0,
        records: [],
      };
    }

    weeks[id].usage += r.usage;
    weeks[id].cost += r.cost;

    // update start/end boundaries
    const currentStart = new Date(weeks[id].start);
    const currentEnd = new Date(weeks[id].end);
    const rDate = new Date(r.start);
    if (rDate < currentStart) weeks[id].start = r.start;
    if (rDate > currentEnd) weeks[id].end = r.start;
  }

  // sort by week number
  return Object.values(weeks).sort((a, b) => a.id - b.id);
}
